import { useEffect, useState } from 'react';
import { createAnonymousAuth } from '../services/squadApi';

export function useAuth() {
  // Initialize from localStorage synchronously if available
  const getInitialUuid = () => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem('supabase_user_id') || localStorage.getItem('attendee_uuid') || '';
  };

  const getInitialToken = () => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem('supabase_auth_token') || '';
  };

  const [attendeeUuid, setAttendeeUuid] = useState(getInitialUuid);
  const [authToken, setAuthToken] = useState(getInitialToken);

  useEffect(() => {
    async function ensureAuthToken() {
      // Check if we already have a token
      const storedToken = localStorage.getItem('supabase_auth_token');
      const storedUserId = localStorage.getItem('supabase_user_id');

      if (storedToken && storedUserId) {
        setAuthToken(storedToken);
        setAttendeeUuid(storedUserId);
        return;
      }

      // Check for fallback UUID
      const fallbackUuid = localStorage.getItem('attendee_uuid');
      if (fallbackUuid) {
        setAttendeeUuid(fallbackUuid);
        return;
      }

      // If no token, try to create one via anonymous auth
      try {
        const { accessToken, userId } = await createAnonymousAuth();
        localStorage.setItem('supabase_auth_token', accessToken);
        localStorage.setItem('supabase_user_id', userId);
        setAuthToken(accessToken);
        setAttendeeUuid(userId);
      } catch (error) {
        // Anonymous auth disabled or failed - use UUID fallback
        console.warn('Anonymous auth not available, using UUID fallback');
        const newFallbackUuid = crypto.randomUUID();
        setAttendeeUuid(newFallbackUuid);
        localStorage.setItem('attendee_uuid', newFallbackUuid);
      }
    }

    // Only run if we don't already have values
    if (!attendeeUuid) {
      ensureAuthToken();
    }
  }, [attendeeUuid]);

  // Listen for storage changes (e.g., when UUID is updated after join)
  useEffect(() => {
    const handleStorageChange = () => {
      const storedUserId = localStorage.getItem('supabase_user_id');
      const storedToken = localStorage.getItem('supabase_auth_token');
      const fallbackUuid = localStorage.getItem('attendee_uuid');
      
      // Update UUID if it changed in localStorage
      if (storedUserId && storedUserId !== attendeeUuid) {
        setAttendeeUuid(storedUserId);
      } else if (fallbackUuid && fallbackUuid !== attendeeUuid && !storedUserId) {
        setAttendeeUuid(fallbackUuid);
      }
      
      // Update token if it changed
      if (storedToken && storedToken !== authToken) {
        setAuthToken(storedToken);
      }
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for changes (in case same-tab updates)
    const interval = setInterval(handleStorageChange, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [attendeeUuid, authToken]);

  return { attendeeUuid, authToken };
}
